/**
 * Protocolo do gateway do Discord (API v10), no subconjunto que o chat usa.
 * https://docs.discord.com/developers/events/gateway
 *
 * Payload: `{"op": n, "d": {...}, "s": seq, "t": "NOME"}`; `s` e `t` só em
 * Dispatch. Fica no contrato porque o cliente (core) fala o mesmo protocolo.
 */

export const GATEWAY_VERSION = 10;

/** Caminho do upgrade, debaixo do prefixo da API para passar pelo mesmo proxy. */
export const GATEWAY_PATH = '/api/chat/gateway';

export const GatewayOpcodes = {
  DISPATCH: 0,
  HEARTBEAT: 1,
  IDENTIFY: 2,
  PRESENCE_UPDATE: 3,
  RESUME: 6,
  RECONNECT: 7,
  INVALID_SESSION: 9,
  HELLO: 10,
  HEARTBEAT_ACK: 11,
} as const;

/** Códigos de fechamento do Discord: 4000+ dizem ao cliente se pode reconectar. */
export const GatewayCloseCodes = {
  UNKNOWN_ERROR: 4000,
  UNKNOWN_OPCODE: 4001,
  DECODE_ERROR: 4002,
  NOT_AUTHENTICATED: 4003,
  AUTHENTICATION_FAILED: 4004,
  ALREADY_AUTHENTICATED: 4005,
  INVALID_SEQ: 4007,
  RATE_LIMITED: 4008,
  SESSION_TIMED_OUT: 4009,
  INVALID_API_VERSION: 4012,
} as const;

/**
 * Fechamentos em que não adianta reconectar: login inválido (4004, o cliente
 * volta para a tela de entrar) e versão errada (4012, precisa atualizar).
 */
export const GATEWAY_FATAL_CLOSE_CODES: ReadonlySet<number> = new Set([
  GatewayCloseCodes.AUTHENTICATION_FAILED,
  GatewayCloseCodes.INVALID_API_VERSION,
]);

export type GatewayPayload<D = unknown> = {
  op: number;
  d?: D;
  s?: number | null;
  t?: string | null;
};

export type GatewayPresenceStatus = 'online' | 'idle' | 'dnd' | 'invisible';
export type GatewayClientKind = 'web' | 'desktop' | 'mobile';

/** `op 10`. */
export type GatewayHello = { heartbeat_interval: number };

/** `op 2`. Sem `token`, vale o cookie de sessão do upgrade (navegador). */
export type GatewayIdentify = {
  token?: string;
  properties: { os?: string; browser?: string; device?: string };
  presence?: {
    status: GatewayPresenceStatus;
    since?: number | null;
    activities?: unknown[];
    afk?: boolean;
  };
};

/** `op 6`. */
export type GatewayResume = {
  token?: string;
  session_id: string;
  seq: number;
};

/** `op 3`. */
export type GatewayPresenceUpdate = {
  status: GatewayPresenceStatus;
  since?: number | null;
  activities?: unknown[];
  afk?: boolean;
};

/** Eventos (Dispatch) que a API manda, com os nomes do Discord. */
export const GatewayDispatchEvents = {
  READY: 'READY',
  READY_SUPPLEMENTAL: 'READY_SUPPLEMENTAL',
  RESUMED: 'RESUMED',
  // Servidor
  GUILD_CREATE: 'GUILD_CREATE',
  GUILD_UPDATE: 'GUILD_UPDATE',
  GUILD_DELETE: 'GUILD_DELETE',
  GUILD_ROLE_CREATE: 'GUILD_ROLE_CREATE',
  GUILD_ROLE_UPDATE: 'GUILD_ROLE_UPDATE',
  GUILD_ROLE_DELETE: 'GUILD_ROLE_DELETE',
  GUILD_MEMBER_ADD: 'GUILD_MEMBER_ADD',
  GUILD_MEMBER_UPDATE: 'GUILD_MEMBER_UPDATE',
  GUILD_MEMBER_REMOVE: 'GUILD_MEMBER_REMOVE',
  GUILD_BAN_ADD: 'GUILD_BAN_ADD',
  GUILD_BAN_REMOVE: 'GUILD_BAN_REMOVE',
  GUILD_EMOJIS_UPDATE: 'GUILD_EMOJIS_UPDATE',
  GUILD_STICKERS_UPDATE: 'GUILD_STICKERS_UPDATE',
  GUILD_AUDIT_LOG_ENTRY_CREATE: 'GUILD_AUDIT_LOG_ENTRY_CREATE',
  GUILD_SCHEDULED_EVENT_CREATE: 'GUILD_SCHEDULED_EVENT_CREATE',
  GUILD_SCHEDULED_EVENT_UPDATE: 'GUILD_SCHEDULED_EVENT_UPDATE',
  GUILD_SCHEDULED_EVENT_DELETE: 'GUILD_SCHEDULED_EVENT_DELETE',
  GUILD_SCHEDULED_EVENT_USER_ADD: 'GUILD_SCHEDULED_EVENT_USER_ADD',
  GUILD_SCHEDULED_EVENT_USER_REMOVE: 'GUILD_SCHEDULED_EVENT_USER_REMOVE',
  AUTO_MODERATION_RULE_CREATE: 'AUTO_MODERATION_RULE_CREATE',
  AUTO_MODERATION_RULE_UPDATE: 'AUTO_MODERATION_RULE_UPDATE',
  AUTO_MODERATION_RULE_DELETE: 'AUTO_MODERATION_RULE_DELETE',
  AUTO_MODERATION_ACTION_EXECUTION: 'AUTO_MODERATION_ACTION_EXECUTION',
  INVITE_CREATE: 'INVITE_CREATE',
  INVITE_DELETE: 'INVITE_DELETE',
  // Canais e threads
  CHANNEL_CREATE: 'CHANNEL_CREATE',
  CHANNEL_UPDATE: 'CHANNEL_UPDATE',
  CHANNEL_DELETE: 'CHANNEL_DELETE',
  CHANNEL_PINS_UPDATE: 'CHANNEL_PINS_UPDATE',
  CHANNEL_RECIPIENT_ADD: 'CHANNEL_RECIPIENT_ADD',
  CHANNEL_RECIPIENT_REMOVE: 'CHANNEL_RECIPIENT_REMOVE',
  THREAD_CREATE: 'THREAD_CREATE',
  THREAD_UPDATE: 'THREAD_UPDATE',
  THREAD_DELETE: 'THREAD_DELETE',
  THREAD_MEMBER_UPDATE: 'THREAD_MEMBER_UPDATE',
  THREAD_MEMBERS_UPDATE: 'THREAD_MEMBERS_UPDATE',
  // Mensagens
  MESSAGE_CREATE: 'MESSAGE_CREATE',
  MESSAGE_UPDATE: 'MESSAGE_UPDATE',
  MESSAGE_DELETE: 'MESSAGE_DELETE',
  MESSAGE_DELETE_BULK: 'MESSAGE_DELETE_BULK',
  MESSAGE_ACK: 'MESSAGE_ACK',
  MESSAGE_REACTION_ADD: 'MESSAGE_REACTION_ADD',
  MESSAGE_REACTION_REMOVE: 'MESSAGE_REACTION_REMOVE',
  MESSAGE_REACTION_REMOVE_ALL: 'MESSAGE_REACTION_REMOVE_ALL',
  MESSAGE_REACTION_REMOVE_EMOJI: 'MESSAGE_REACTION_REMOVE_EMOJI',
  MESSAGE_POLL_VOTE_ADD: 'MESSAGE_POLL_VOTE_ADD',
  MESSAGE_POLL_VOTE_REMOVE: 'MESSAGE_POLL_VOTE_REMOVE',
  TYPING_START: 'TYPING_START',
  // Pessoa
  PRESENCE_UPDATE: 'PRESENCE_UPDATE',
  USER_SETTINGS_UPDATE: 'USER_SETTINGS_UPDATE',
  USER_GUILD_SETTINGS_UPDATE: 'USER_GUILD_SETTINGS_UPDATE',
  USER_NOTE_UPDATE: 'USER_NOTE_UPDATE',
  USER_UPDATE: 'USER_UPDATE',
} as const;

export type GatewayDispatchEvent =
  (typeof GatewayDispatchEvents)[keyof typeof GatewayDispatchEvents];
